import React from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Clock, Calendar, ChevronDown, ChevronUp, Palmtree, 
  Heart, GraduationCap, CalendarDays 
} from 'lucide-react';

// Constants
const TASK_HEIGHT = 20;
const TASK_GAP = 4;
const LANE_PADDING = 12;
const ABSENCE_LANE_HEIGHT = 28;
const MIN_ROW_HEIGHT = 70;

/**
 * Helper: Get absence icon based on type
 */
const getAbsenceIcon = (type) => {
  switch (type) {
    case 'vacation': return <Palmtree className="w-3 h-3" />;
    case 'sick': return <Heart className="w-3 h-3" />;
    case 'training': return <GraduationCap className="w-3 h-3" />;
    case 'holiday': return <CalendarDays className="w-3 h-3" />;
    default: return <Calendar className="w-3 h-3" />;
  }
};

/**
 * Helper: Get absence label based on type
 */
const getAbsenceLabel = (type) => {
  const labels = {
    vacation: 'Urlaub',
    sick: 'Krankheit',
    training: 'Weiterbildung',
    holiday: 'Feiertag',
    other: 'Sonstige'
  };
  return labels[type] || type;
};

/**
 * Helper: Get status badge for utilization
 */
const getStatusBadge = (status, label) => {
  const styles = {
    overbooked: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    normal: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    low: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.normal}`}>
      {label}
    </span>
  );
};

/**
 * Helper: Calculate lane height based on overlapping tasks
 */
const calculateLaneHeight = (lane) => {
  const sortedTasks = [...lane.tasks].sort((a, b) => 
    new Date(a.start_date) - new Date(b.start_date)
  );
  const taskRows = [];
  
  sortedTasks.forEach(task => {
    const taskStart = new Date(task.start_date);
    const taskEnd = new Date(task.end_date);
    let assignedRow = 0;
    
    for (let row = 0; row < taskRows.length; row++) {
      const rowTasks = taskRows[row];
      const hasOverlap = rowTasks.some(rt => {
        const rtStart = new Date(rt.start_date);
        const rtEnd = new Date(rt.end_date);
        return taskStart <= rtEnd && taskEnd >= rtStart;
      });
      if (!hasOverlap) {
        assignedRow = row;
        break;
      }
      assignedRow = row + 1;
    }
    
    if (!taskRows[assignedRow]) taskRows[assignedRow] = [];
    taskRows[assignedRow].push(task);
  });
  
  const rowCount = Math.max(taskRows.length, 1);
  return rowCount * 24 + 14;
};

/**
 * Helper: Get priority badge configuration
 */
const getPriorityBadge = (priority) => {
  if (priority === 'high') return { label: '!', bg: '#ef4444', title: 'Hohe Priorität' };
  if (priority === 'low') return { label: '↓', bg: '#94a3b8', title: 'Niedrige Priorität' };
  return null;
};

/**
 * EmployeeTimelineRow - Displays a single employee's timeline row
 */
const EmployeeTimelineRow = ({
  employee,
  utilization,
  projectLanes,
  timelineDays,
  dayWidth,
  expandedHourLanes,
  planningSettings,
  // Callbacks
  getBarPosition,
  toggleHourLane,
  openCapacityDialog,
  openAbsenceDialog,
  handleAdjustAllUserTasks,
  handleAdjustTaskForAbsence
}) => {
  // Calculate total lanes height
  const totalLanesHeight = projectLanes.length > 0 
    ? projectLanes.reduce((acc, lane) => acc + calculateLaneHeight(lane), 0)
    : 0;
  
  // Calculate row height
  const rowHeight = Math.max(totalLanesHeight + ABSENCE_LANE_HEIGHT + 8, MIN_ROW_HEIGHT);
  
  // Check if any task has unadjusted absence conflict
  const hasUnadjustedConflicts = projectLanes.some(lane => 
    lane.tasks.some(task => {
      if (task.absence_adjusted) return false;
      const taskStart = new Date(task.start_date);
      const taskEnd = new Date(task.end_date);
      return employee.absences?.some(abs => {
        const absStart = new Date(abs.start_date);
        const absEnd = new Date(abs.end_date);
        return taskStart <= absEnd && taskEnd >= absStart;
      });
    })
  );

  // Pre-calculate all lane positions for rendering
  const calculateRenderedLanes = () => {
    let currentTop = 4;
    
    return projectLanes.map((lane) => {
      const projectPos = getBarPosition(lane.project_start, lane.project_end);
      const laneStartTop = currentTop;
      
      // Calculate task rows for overlapping tasks
      const taskRows = [];
      const sortedTasks = [...lane.tasks].sort((a, b) => 
        new Date(a.start_date) - new Date(b.start_date)
      );
      
      sortedTasks.forEach(task => {
        const taskStart = new Date(task.start_date);
        const taskEnd = new Date(task.end_date);
        
        let assignedRow = 0;
        for (let row = 0; row < taskRows.length; row++) {
          const rowTasks = taskRows[row];
          const hasOverlap = rowTasks.some(rt => {
            const rtStart = new Date(rt.start_date);
            const rtEnd = new Date(rt.end_date);
            return taskStart <= rtEnd && taskEnd >= rtStart;
          });
          
          if (!hasOverlap) {
            assignedRow = row;
            break;
          }
          assignedRow = row + 1;
        }
        
        if (!taskRows[assignedRow]) taskRows[assignedRow] = [];
        taskRows[assignedRow].push({ ...task, _row: assignedRow });
      });
      
      const taskRowCount = Math.max(taskRows.length, 1);
      const laneHeight = LANE_PADDING + (taskRowCount * (TASK_HEIGHT + TASK_GAP));
      
      currentTop += laneHeight;
      
      return {
        lane,
        projectPos,
        laneStartTop,
        laneHeight,
        tasksWithRows: taskRows.flat()
      };
    });
  };

  const renderedLanes = calculateRenderedLanes();

  // Calculate daily hours data for expanded view
  const calculateDailyData = () => {
    const employeeWeeklyHours = employee.weekly_hours || 42.5;
    const employeeDailyHours = employeeWeeklyHours / 5;
    const { project_work_percent } = planningSettings;
    const adminPercent = 100 - project_work_percent;
    const availableProjectHours = employeeDailyHours * (project_work_percent / 100);
    const adminHoursPerDay = employeeDailyHours * (adminPercent / 100);
    
    return timelineDays.map(day => {
      const currentDay = new Date(day.date);
      currentDay.setHours(0, 0, 0, 0);
      
      // Check absences
      let absenceHours = 0;
      let isAbsent = false;
      (employee.absences || []).forEach(absence => {
        const absStart = new Date(absence.start_date);
        const absEnd = new Date(absence.end_date);
        absStart.setHours(0, 0, 0, 0);
        absEnd.setHours(0, 0, 0, 0);
        
        if (currentDay >= absStart && currentDay <= absEnd) {
          isAbsent = true;
          const isHalfDay = (currentDay.getTime() === absStart.getTime() && absence.half_day_start) ||
                            (currentDay.getTime() === absEnd.getTime() && absence.half_day_end);
          absenceHours = isHalfDay ? employeeDailyHours / 2 : employeeDailyHours;
        }
      });
      
      if (isAbsent || day.isWeekend) {
        return {
          date: day.date,
          projectHours: 0,
          adminHours: 0,
          absenceHours: day.isWeekend ? 0 : absenceHours,
          totalHours: day.isWeekend ? 0 : absenceHours,
          isWeekend: day.isWeekend,
          isAbsent: !day.isWeekend && isAbsent
        };
      }
      
      let projectHours = 0;
      let totalAllocationPercent = 0;
      
      projectLanes.forEach(lane => {
        lane.tasks.forEach(task => {
          const taskStart = new Date(task.start_date);
          const taskEnd = new Date(task.end_date);
          taskStart.setHours(0,0,0,0);
          taskEnd.setHours(0,0,0,0);
          
          if (currentDay >= taskStart && currentDay <= taskEnd) {
            const taskHours = availableProjectHours * ((task.allocation_percent || 100) / 100);
            projectHours += taskHours;
            totalAllocationPercent += (task.allocation_percent || 100);
          }
        });
      });
      
      const hasProjectWork = projectHours > 0;
      const effectiveAllocationForAdmin = Math.min(totalAllocationPercent, 100);
      const adminHours = hasProjectWork ? adminHoursPerDay * (effectiveAllocationForAdmin / 100) : 0;
      
      return {
        date: day.date,
        projectHours,
        adminHours,
        absenceHours: 0,
        totalHours: projectHours + adminHours,
        isWeekend: day.isWeekend,
        isAbsent: false
      };
    });
  };

  return (
    <div key={employee.user_id}>
      {/* Main Row */}
      <div className="flex border-b border-slate-300 dark:border-slate-700 hover:bg-muted/5">
        {/* Employee Info */}
        <div className="w-[250px] shrink-0 border-r border-slate-300 dark:border-slate-700 p-2 overflow-hidden" style={{ height: `${rowHeight}px` }}>
          <div className="h-full flex flex-col">
            {/* Top: Employee name, hours, utilization */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                  {employee.user_name?.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{employee.user_name}</p>
                  <div className="flex items-center gap-1">
                    <p className="text-[10px] text-muted-foreground">
                      {employee.weekly_hours}h/Woche
                    </p>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-4 w-4 p-0"
                      onClick={() => openCapacityDialog(utilization || employee)}
                      title="Kapazität bearbeiten"
                    >
                      <Clock className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="shrink-0">
                {utilization && getStatusBadge(utilization.status, `${utilization.utilization_percent}%`)}
              </div>
            </div>
            
            <div className="flex-1" />
            
            {/* Bottom: Controls */}
            <div className="flex items-center justify-between">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-5 px-1 text-[10px] text-muted-foreground"
                onClick={() => toggleHourLane(employee.user_id)}
                title="Stunden anzeigen"
              >
                {expandedHourLanes[employee.user_id] ? (
                  <ChevronUp className="w-3 h-3 mr-1" />
                ) : (
                  <ChevronDown className="w-3 h-3 mr-1" />
                )}
                Stunden
              </Button>
              
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6"
                  onClick={() => openAbsenceDialog(employee)}
                  title="Abwesenheit hinzufügen"
                >
                  <Palmtree className="w-3 h-3" />
                </Button>
                {hasUnadjustedConflicts && (
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 text-orange-500 hover:text-orange-600 hover:bg-orange-50"
                    onClick={() => handleAdjustAllUserTasks(employee.user_id, employee.user_name)}
                    title="Tasks mit Abwesenheitskonflikten anpassen"
                  >
                    <Calendar className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Timeline */}
        <div className="relative" style={{ height: `${rowHeight}px`, width: `${timelineDays.length * dayWidth}px` }}>
          {/* Project Lanes with Tasks */}
          {renderedLanes.map(({ lane, projectPos, laneStartTop, laneHeight, tasksWithRows }) => (
            <div key={lane.project_id} className="absolute w-full" style={{ top: `${laneStartTop}px`, height: `${laneHeight}px` }}>
              {/* Project background bar */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="absolute rounded-md border-2 border-dashed"
                      style={{
                        left: projectPos.left,
                        width: projectPos.width,
                        top: 0,
                        height: `${laneHeight - 2}px`,
                        backgroundColor: `${lane.color}15`,
                        borderColor: `${lane.color}40`,
                        zIndex: 1
                      }}
                    >
                      <span 
                        className="absolute left-1 top-0 text-[9px] font-medium truncate px-1"
                        style={{ color: lane.color, maxWidth: 'calc(100% - 8px)' }}
                      >
                        {lane.project_name}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-sm">
                      <p className="font-medium">{lane.project_name}</p>
                      <p className="text-muted-foreground">{lane.project_start} - {lane.project_end}</p>
                      <p>{lane.tasks.length} Tasks</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {/* Task bars */}
              {tasksWithRows.map((task) => {
                const taskPos = getBarPosition(task.start_date, task.end_date);
                const taskTop = LANE_PADDING + (task._row * (TASK_HEIGHT + TASK_GAP));
                
                // Check absence overlap
                const taskStart = new Date(task.start_date);
                const taskEnd = new Date(task.end_date);
                const overlappingAbsences = employee.absences?.filter(abs => {
                  const absStart = new Date(abs.start_date);
                  const absEnd = new Date(abs.end_date);
                  return taskStart <= absEnd && taskEnd >= absStart;
                }) || [];
                const hasAbsenceOverlap = overlappingAbsences.length > 0 && !task.absence_adjusted;
                const priorityBadge = getPriorityBadge(task.priority);
                
                return (
                  <TooltipProvider key={task.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={`absolute rounded cursor-pointer hover:opacity-90 transition-opacity flex items-center px-1 text-[10px] text-white font-medium overflow-hidden shadow-sm ${hasAbsenceOverlap ? 'ring-2 ring-orange-400 ring-offset-1' : ''}`}
                          style={{
                            left: taskPos.left,
                            width: taskPos.width,
                            top: `${taskTop}px`,
                            height: `${TASK_HEIGHT}px`,
                            backgroundColor: lane.color,
                            opacity: task.allocation_percent < 100 ? 0.8 : 1,
                            zIndex: 3
                          }}
                        >
                          {priorityBadge && (
                            <span 
                              className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[9px] mr-1 font-bold"
                              style={{ backgroundColor: priorityBadge.bg }}
                              title={priorityBadge.title}
                            >
                              {priorityBadge.label}
                            </span>
                          )}
                          {hasAbsenceOverlap && (
                            <span className="flex-shrink-0 mr-1" title="Überschneidung mit Abwesenheit!">⚠️</span>
                          )}
                          <span className="truncate">{task.title}</span>
                          {task.allocation_percent < 100 && (
                            <span className="ml-1 opacity-80 flex-shrink-0">({task.allocation_percent}%)</span>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <div className="text-sm">
                          <p className="font-medium">{task.title}</p>
                          <p className="text-muted-foreground">{task.start_date} - {task.end_date}</p>
                          <p>Allokation: {task.allocation_percent}%</p>
                          <div className="flex items-center gap-1">
                            <span>Priorität:</span>
                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                              task.priority === 'high' ? 'bg-red-100 text-red-700' :
                              task.priority === 'low' ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {task.priority === 'high' ? 'Hoch' : task.priority === 'low' ? 'Niedrig' : 'Mittel'}
                            </span>
                          </div>
                          <p>Status: {task.status}</p>
                          {task.absence_adjusted && (
                            <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded text-green-700 dark:text-green-300 text-xs">
                              <p className="font-medium">✓ Bereits für Abwesenheit angepasst</p>
                              <p>{task.absence_adjustment_note}</p>
                            </div>
                          )}
                          {hasAbsenceOverlap && (
                            <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-orange-700">
                              <p className="font-medium">⚠️ Überschneidung mit Abwesenheit!</p>
                              {overlappingAbsences.map((abs, i) => (
                                <p key={i} className="text-xs">
                                  {getAbsenceLabel(abs.absence_type)}: {abs.start_date} - {abs.end_date}
                                </p>
                              ))}
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-2 w-full text-xs h-7 bg-background"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAdjustTaskForAbsence(task.id);
                                }}
                              >
                                Task-Datum anpassen
                              </Button>
                            </div>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>
          ))}
          
          {/* Absence Lane */}
          <div 
            className="absolute w-full"
            style={{ top: `${totalLanesHeight}px`, height: `${ABSENCE_LANE_HEIGHT}px` }}
          >
            {employee.absences?.map((absence) => {
              const pos = getBarPosition(absence.start_date, absence.end_date);
              return (
                <TooltipProvider key={absence.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="absolute h-5 rounded cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1 px-2 text-xs text-white"
                        style={{
                          left: pos.left,
                          width: pos.width,
                          top: '4px',
                          backgroundColor: absence.color,
                          zIndex: 5
                        }}
                        onClick={() => openAbsenceDialog(employee, absence)}
                      >
                        {getAbsenceIcon(absence.absence_type)}
                        <span className="truncate">{getAbsenceLabel(absence.absence_type)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="text-sm">
                        <p className="font-medium">{getAbsenceLabel(absence.absence_type)}</p>
                        <p>{absence.start_date} - {absence.end_date}</p>
                        {absence.notes && <p className="text-muted-foreground">{absence.notes}</p>}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Expandable Hours Lane */}
      {expandedHourLanes[employee.user_id] && (
        <ExpandedHoursLane
          employee={employee}
          projectLanes={projectLanes}
          timelineDays={timelineDays}
          dayWidth={dayWidth}
          planningSettings={planningSettings}
          calculateDailyData={calculateDailyData}
        />
      )}
    </div>
  );
};

/**
 * HoursRow - Helper component for displaying a row of hours
 */
const HoursRow = ({ label, colorClass, dailyData, dayWidth, getValue, showOverbooked, maxValue }) => (
  <div className="flex">
    <div className="w-[250px] shrink-0 border-r border-slate-300 dark:border-slate-700 px-2 py-0.5 flex items-center">
      <span className={`text-[9px] ${colorClass} font-medium`}>{label}</span>
    </div>
    <div className="flex">
      {dailyData.map((d, idx) => {
        const value = getValue(d);
        const isOverbooked = showOverbooked && maxValue && value > maxValue;
        
        return (
          <div 
            key={idx}
            className={`flex items-center justify-center text-[8px] border-r border-slate-300 dark:border-slate-700 h-5 ${d.isWeekend ? 'bg-slate-200/70 dark:bg-slate-700/50' : ''} ${d.isAbsent ? 'bg-green-100 dark:bg-green-900/40' : ''}`}
            style={{ width: `${dayWidth}px` }}
          >
            {value > 0 && (
              <span className={isOverbooked ? 'text-red-600 font-bold' : colorClass.includes('font-bold') ? 'font-bold' : ''}>
                {value.toFixed(1)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

/**
 * ExpandedHoursLane - Shows detailed hours breakdown per day
 */
const ExpandedHoursLane = ({ 
  employee, 
  timelineDays, 
  dayWidth, 
  planningSettings,
  calculateDailyData 
}) => {
  const employeeWeeklyHours = employee.weekly_hours || 42.5;
  const employeeDailyHours = employeeWeeklyHours / 5;
  const { project_work_percent } = planningSettings;
  const adminPercent = 100 - project_work_percent;
  const availableProjectHours = employeeDailyHours * (project_work_percent / 100);
  
  const dailyData = calculateDailyData();

  return (
    <div className="flex flex-col border-b border-slate-300 dark:border-slate-700 bg-slate-50/30 dark:bg-slate-800/30">
      <HoursRow 
        label={`Projekt (${project_work_percent}%)`}
        colorClass="text-blue-600"
        dailyData={dailyData}
        dayWidth={dayWidth}
        getValue={(d) => !d.isWeekend && !d.isAbsent ? d.projectHours : 0}
        showOverbooked={true}
        maxValue={availableProjectHours}
      />
      <HoursRow 
        label={`Admin (${adminPercent}%)`}
        colorClass="text-slate-500"
        dailyData={dailyData}
        dayWidth={dayWidth}
        getValue={(d) => !d.isWeekend && !d.isAbsent ? d.adminHours : 0}
      />
      <HoursRow 
        label="Abwesend"
        colorClass="text-green-600"
        dailyData={dailyData}
        dayWidth={dayWidth}
        getValue={(d) => d.isAbsent ? d.absenceHours : 0}
      />
      <div className="flex border-t border-slate-300 dark:border-slate-700">
        <div className="w-[250px] shrink-0 border-r border-slate-300 dark:border-slate-700 px-2 py-0.5 flex items-center">
          <span className="text-[9px] text-slate-700 font-bold">Total ({employeeDailyHours.toFixed(1)}h/Tag)</span>
        </div>
        <div className="flex">
          {dailyData.map((d, idx) => {
            const isOverbooked = d.totalHours > employeeDailyHours;
            return (
              <div 
                key={idx}
                className={`flex items-center justify-center text-[8px] border-r border-slate-300 dark:border-slate-700 h-5 ${d.isWeekend ? 'bg-slate-200/70 dark:bg-slate-700/50' : ''} ${d.isAbsent ? 'bg-green-100 dark:bg-green-900/40' : ''}`}
                style={{ width: `${dayWidth}px` }}
              >
                {!d.isWeekend && d.totalHours > 0 && (
                  <span className={`font-bold ${isOverbooked ? 'text-red-600' : 'text-slate-700'}`}>
                    {d.totalHours.toFixed(1)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex bg-purple-50/30">
        <div className="w-[250px] shrink-0 border-r border-slate-300 dark:border-slate-700 px-2 py-0.5 flex items-center">
          <span className="text-[9px] text-purple-600 font-medium">Frei (planbar)</span>
        </div>
        <div className="flex">
          {dailyData.map((d, idx) => {
            const freeHours = d.isWeekend || d.isAbsent ? 0 : Math.max(0, employeeDailyHours - d.totalHours);
            return (
              <div 
                key={idx}
                className={`flex items-center justify-center text-[8px] border-r border-slate-300 dark:border-slate-700 h-5 ${d.isWeekend ? 'bg-slate-200/70 dark:bg-slate-700/50' : ''} ${d.isAbsent ? 'bg-green-100 dark:bg-green-900/40' : ''}`}
                style={{ width: `${dayWidth}px` }}
              >
                {freeHours > 0 && (
                  <span className="text-purple-600 font-medium">{freeHours.toFixed(1)}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EmployeeTimelineRow;
